import { FilterMultiSelect, Pagination, ProjectThumb, stageLabel } from '@/components/ui';
import type { Stage } from '@/components/ui';
import { NavIcon } from '@/components/icons/NavIcon';
import type { Level, ProjectListItem } from '@/api/types';
import { levelLabel, projectLabel } from '@/lib/format';
import LevelSegment from './LevelSegment';
import TruncatedName from './TruncatedName';
import type { SelectedGame } from './HeldGamesList';
import { STAGE_FILTER_OPTIONS } from '@/lib/stageFilter';
import type { FilterOption } from '@/components/ui/FilterSelect';
import type { StageFilterValue } from '@/lib/stageFilter';

import styles from '../MemberPerms.module.css';

interface GameGrantListProps {
  loading: boolean;
  projects: ProjectListItem[];
  // 'all' grant 보유 / 모든 게임 옵션 체크 중 — 개별 편집 비활성 표시.
  hasAllScope: boolean;
  disabled?: boolean;
  stageOptions?: FilterOption[];
  // 'all' grant의 level (via all일 때 세그먼트 표시용).
  allScopeLevel: Level | null;
  // 이 페이지에서 이미 보유 권한 쪽으로 이동해 숨긴 게임 수 — 페이지가 비어 보일 때 안내용.
  hiddenCount: number;
  // 보유 권한에서 빼낸 저장된 게임 — 검색·페이지와 무관하게 목록 위에 고정해 '회수 예정'으로 표시.
  pendingRevokes: SelectedGame[];
  // 회수 예정 게임을 다시 보유 권한으로 (회수 취소). level은 되돌릴 역할.
  onRestoreRevoke: (projectId: string, level: Level) => void;
  // 역할 버튼 클릭 — 그 역할로 바로 보유 권한에 추가 (목록에서는 빠진다). 행 클릭·기본 역할은 없다.
  onAddProjectWithLevel: (projectId: string, level: Level) => void;
  // 게임 검색 — 헤더 아래 입력. 서버 검색(useProjects q)에 연동.
  searchQ: string;
  onChangeSearchQ: (value: string) => void;
  // Stage 필터 — 서버 필터(useProjects stage)에 연동. 기본값은 Stage 2.
  stages: StageFilterValue[];
  onChangeStages: (values: StageFilterValue[]) => void;
  // 남은 게임을 지정한 역할로 한 번에 넣기.
  onSelectAll: (level: Level) => void;
  bulkLoading: boolean;
  bulkError: string | null;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  disabledMessage?: string;
}

// 게임별 권한 추가 목록 — 보유 권한에 없는 게임만 보이며, 행을 고르면 보유 권한 박스로 이동한다.
// (박스 간에 아이템을 넣고 빼는 전송 목록 UX. 되돌리기는 보유 권한 쪽 '선택 해제'가 담당.)
export default function GameGrantList({
  loading,
  projects,
  hasAllScope,
  disabled: controlsDisabled = false,
  stageOptions = STAGE_FILTER_OPTIONS,
  allScopeLevel,
  hiddenCount,
  pendingRevokes,
  onRestoreRevoke,
  onAddProjectWithLevel,
  searchQ,
  onChangeSearchQ,
  stages,
  onChangeStages,
  onSelectAll,
  bulkLoading,
  bulkError,
  total,
  page,
  pageSize,
  onPageChange,
  disabledMessage,
}: GameGrantListProps) {
  const selectionLocked = hasAllScope || controlsDisabled;
  return (
    <>
      {hasAllScope && <p className={styles.permissionMeaning}>
        <NavIcon name="lock" size={15} />
        <span>모든 게임 자동 부여가 선택되어 게임별 체크와 역할 변경이 잠겨 있습니다. 목록 검색과 Stage 필터는 사용할 수 있습니다.</span>
      </p>}
      <section className={`${styles.permissionBrowser} ${styles.gameAddBrowser}`} aria-label="게임별 권한 설정">
      <div className={styles.permissionGroupHead}>
        <strong>권한 추가</strong>
        <span>{total}</span>
      </div>
      {/* 툴바 한 줄: Stage 다중 선택 · 검색 · 모두 넣기. 보유 권한 열의 툴바와 같은 높이·간격. */}
      <div className={styles.listToolbar} data-tour="game-toolbar"
        style={controlsDisabled ? { opacity: 0.45, pointerEvents: 'none' } : undefined}>
        <FilterMultiSelect
          label="Stage"
          values={stages}
          options={stageOptions}
          onChange={values => onChangeStages(values as StageFilterValue[])}
        />
        <label className={`${styles.toolbarSearch} ${controlsDisabled ? styles.permissionSearchDisabled : ''}`}>
          <NavIcon name="search" size={14} />
          <input
            type="text"
            disabled={controlsDisabled}
            value={searchQ}
            onChange={(e) => onChangeSearchQ(e.target.value)}
            placeholder="게임 검색 (이름 · prefix · id)"
            aria-label="게임 검색"
          />
        </label>
      </div>
      {bulkError && <p role="alert">{bulkError}</p>}

      {/* 일괄 동작 행 — 게임 행처럼 놓이지만 점선 테두리·회색 배경으로 목록 항목이 아님을 드러낸다. */}
      <div className={styles.bulkRow} data-tour="game-bulk"
        style={controlsDisabled ? { opacity: 0.45, pointerEvents: 'none' } : undefined}>
        <span className={styles.bulkIcon} aria-hidden="true"><NavIcon name="grid" size={16} /></span>
        <div className={styles.bulkText}>
          <strong>전체 추가</strong>
          <span>검색 결과 {total}개</span>
        </div>
        <div className={styles.gameRight}>
          <LevelSegment value={null}
            disabled={selectionLocked || loading || bulkLoading || !total}
            groupLabel="전체 추가 역할"
            buttonLabel={lv => `전체 ${levelLabel(lv)}로 추가 (${total})`}
            onSelect={onSelectAll} />
        </div>
      </div>

      {/* 회수 예정 — 보유 권한에서 빼낸 저장 게임. 저장하면 회수되고, '+'로 다시 넣으면 취소된다. */}
      {pendingRevokes.length > 0 && <div className={`${styles.gameList} ${styles.revokeList}`}
        style={{ opacity: controlsDisabled ? 0.45 : 1 }} aria-label="권한 회수 게임">
        {pendingRevokes.map(g => {
          const label = g.displayName || g.prefix;
          return <div key={g.projectId} className={`${styles.gameRow} ${styles.gameAddRow} ${styles.gameRevokeRow}`}>
            <ProjectThumb prefix={g.prefix} display_name={g.displayName} iconUrl={g.iconUrl} size={48} />
            <div className={styles.gameInfo}>
              <div className={`${styles.gameName} ${styles.gameNameRow}`}>
                <TruncatedName text={label} />
              </div>
              {g.stage && <div className={styles.gameStageRow}>{stageLabel(g.stage as Stage)}</div>}
            </div>
            <div className={styles.gameRight} onClick={(e) => e.stopPropagation()}>
              <span className={`${styles.changeBadge} ${styles.revoke}`}>권한 회수</span>
              <LevelSegment value={g.level} disabled={controlsDisabled}
                groupLabel={`${label} 역할`}
                buttonLabel={lv => `${label} ${levelLabel(lv)}로 회수 취소`}
                onSelect={lv => onRestoreRevoke(g.projectId, lv)} />
              {/* 회수 취소는 보유 행의 X와 같은 버튼 — 누르면 원래 역할로 보유 권한에 복귀 */}
              <button type="button" className={styles.removeBtn} disabled={controlsDisabled}
                onClick={() => onRestoreRevoke(g.projectId, g.level)}
                aria-label={`${label} 회수 취소`} title="회수 취소 (보유 권한으로 되돌리기)">
                <NavIcon name="x" size={14} color="#9B9B9B" />
              </button>
            </div>
          </div>;
        })}
      </div>}

      {/* 게임 리스트 */}
      <div
        className={styles.gameList}
        style={{ opacity: controlsDisabled ? 0.45 : 1, marginTop: 12 }}
      >
        {loading ? (
          // 검색·페이지네이션 로딩 — 텍스트 대신 스켈레톤 행(메인 프로젝트 목록과 동일 방식).
          Array.from({ length: 6 }).map((_, i) => (
            <div key={`sk-${i}`} className={styles.gameRowSkel} aria-hidden="true">
              <span className={`${styles.skel} ${styles.skelCheckbox}`} />
              <span className={`${styles.skel} ${styles.skelThumb}`} />
              <div className={styles.gameInfo}>
                <div className={`${styles.skel} ${styles.skelLine}`} style={{ width: '55%', height: 13 }} />
                <div
                  className={`${styles.skel} ${styles.skelLine}`}
                  style={{ width: '35%', height: 10, marginTop: 8 }}
                />
              </div>
              <span className={`${styles.skel} ${styles.skelSeg}`} />
            </div>
          ))
        ) : projects.length === 0 ? (
          <div className={styles.emptyMsg}>
            {disabledMessage ? null : hiddenCount > 0
              ? `조건에 맞는 게임 ${hiddenCount}개가 모두 보유 권한에 있어요`
              : '표시할 게임이 없어요'}
          </div>
        ) : (
          projects.map((p) => {
          // project_id가 없는(=gcp 미연동) 게임은 grant 부여 불가 — 선택 비활성화 표시.
          const projectId = p.project_id;
          const disabled = projectId === null || selectionLocked;
          // via all이면 모든 게임이 이미 부여된 것처럼 표시 (편집은 위에서 비활성).
          const viaAll = hasAllScope;
          const label = projectLabel(p.display_name, p.prefix);
          return (
            <div
              key={p.project_id ?? p.prefix}
              className={`${styles.gameRow} ${styles.gameAddRow}`}
              style={projectId === null ? { opacity: 0.45 } : undefined}
              title={projectId === null ? '게임 데이터 미연동 — 권한 부여 불가' : undefined}
            >
              <ProjectThumb
                prefix={p.prefix}
                display_name={p.display_name}
                iconUrl={p.icon_url ?? null}
                size={48}
              />
              <div className={styles.gameInfo}>
                {/* 이름 아래에 Stage를 메타 텍스트로 둔다. prefix·OS·멤버 수는 표시하지 않는다. */}
                <div className={`${styles.gameName} ${styles.gameNameRow}`}>
                  <TruncatedName text={label} />
                </div>
                {p.stage && <div className={styles.gameStageRow}>{stageLabel(p.stage as Stage)}</div>}
              </div>

              {/* 우측: 역할 버튼 — 누르면 그 역할로 바로 보유 권한에 추가 */}
              <div className={styles.gameRight} onClick={(e) => e.stopPropagation()}>
                {viaAll && <span className={styles.viaAll}>기존 공통 권한</span>}
                {projectId !== null && (
                  <LevelSegment
                    value={viaAll ? allScopeLevel : null}
                    disabled={disabled}
                    dimmed={selectionLocked}
                    groupLabel={`${label} 역할`}
                    buttonLabel={lv => `${label} ${levelLabel(lv)}로 추가`}
                    onSelect={lv => onAddProjectWithLevel(projectId, lv)}
                  />
                )}
              </div>
            </div>
          );
          })
        )}
      </div>
      {total > pageSize && <div className={styles.permissionGamePagination}>
        <Pagination page={page} total={total} pageSize={pageSize} onChange={onPageChange} />
      </div>}
      </section>
    </>
  );
}
